import { Component, ViewChild, OnInit } from '@angular/core';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import { EmailSttingsService, NotificationTemplate, SendNotificationPayload } from '../../core/services/email-sttings.service';
import { ToastService } from '../../core/services/toast.service';
import { LazyLoadEvent } from 'primeng/api/lazyloadevent';
import { Table } from 'primeng/table';

@Component({
  selector: 'app-email-sttings',
  templateUrl: './email-sttings.component.html',
  styleUrls: ['./email-sttings.component.scss']
})
export class EmailSttingsComponent implements OnInit {

  // CKEditor
  public Editor = ClassicEditor as any;
  public editorConfig = {
    toolbar: {
      items: [
        'heading',
        '|',
        'bold',
        'italic',
        'link',
        'bulletedList',
        'numberedList',
        '|',
        'undo',
        'redo'
      ]
    }
  };
  emailBody: string = '';
  smsBody: string = '';
  whatsappBody: string = '';

  // Template Management Properties
  templates: NotificationTemplate[] = [];
  selectedTemplate: NotificationTemplate | null = null;
  templateCode: string = '';
  templateName: string = '';
  templateDescription: string = '';
  emailSubject: string = '';

  // Notification Send Properties
  recipient: string = '';
  variables: Record<string, any> = {};
  requiredVariables: string[] = [];

  // Notification Logs
  notificationLogs: any = [];
  showLogs: boolean = false;

  // UI State
  isLoading: boolean = false;
  showTemplateForm: boolean = false;
  activeTab: string = 'manage'; // 'manage', 'send', 'logs'
  channels = ['email', 'sms', 'whatsapp'];
  selectedChannel: string = 'email';
  @ViewChild('dt') table!: Table;


  constructor(
    private emailService: EmailSttingsService,
    private toastService: ToastService
  ) { }

  ngOnInit() {
    this.loadTemplates();
  }

  loadTemplates() {
    this.isLoading = true;
    this.emailService.getTemplates().subscribe(
      (response) => {
        this.templates = response || [];
        this.templateCodeOptions = [
          { label: 'All', value: null },
          ...(response || []).map((temp: any) => ({
            label: temp.code,
            value: temp.code
          }))
        ];
        this.isLoading = false;
      },
      () => {
        this.isLoading = false;
      }
    );
  }

  toggleTemplateForm() {
    this.showTemplateForm = !this.showTemplateForm;
    if (!this.showTemplateForm) {
      this.resetTemplateForm();
    }
  }

  resetTemplateForm() {
    this.templateCode = '';
    this.templateName = '';
    this.templateDescription = '';
    this.emailSubject = '';
    this.emailBody = '';
    this.smsBody = '';
    this.whatsappBody = '';
    this.selectedTemplate = null;
  }

  editTemplate(template: NotificationTemplate) {
    this.selectedTemplate = template;
    this.templateCode = template.code;
    this.templateName = template.name;
    this.templateDescription = template.description;
    this.emailSubject = template.email?.subject || '';
    this.emailBody = template.email?.body || '';
    this.smsBody = template.sms?.body || '';
    this.whatsappBody = template.whatsapp?.body || '';
    this.showTemplateForm = true;
  }

  async saveTemplate() {
    if (!this.templateCode.trim()) {
      this.toastService.showToaster('Validation', 'Please enter template code', 'error');
      return;
    }

    if (!this.templateName.trim()) {
      this.toastService.showToaster('Validation', 'Please enter template name', 'error');
      return;
    }

    try {
      this.isLoading = true;
      const emailHtml = await this.getEmailHtml();

      const template: NotificationTemplate = {
        code: this.templateCode,
        name: this.templateName,
        description: this.templateDescription,
        email: {
          subject: this.emailSubject,
          body: emailHtml
        }
      };

      if (this.smsBody.trim()) {
        template.sms = { body: this.smsBody };
      }

      if (this.whatsappBody.trim()) {
        template.whatsapp = { body: this.whatsappBody };
      }

      this.emailService.createOrUpdateTemplate(template).subscribe(
        () => {
          this.isLoading = false;
          this.toastService.showToaster('Success', 'Template saved successfully', 'success');
          this.loadTemplates();
          this.resetTemplateForm();
          this.showTemplateForm = false;
        },
        () => {
          this.isLoading = false;
        }
      );
    } catch (error) {
      this.isLoading = false;
      this.toastService.showToaster('Error', 'Error preparing template: ' + error, 'error');
    }
  }

  // Delete template
  deleteTemplate(code: string) {
    if (!confirm(`Are you sure you want to delete template "${code}"?`)) {
      return;
    }
    this.toastService.showToaster('Success', 'Coming soon.', 'success');
  }

  // Get HTML from editor

  getEmailHtml(): Promise<string> {
    return Promise.resolve(this.emailBody || '');
  }

  // ==================== NOTIFICATION SENDING ====================

  // Toggle send form and load template variables
  toggleSendForm(template?: NotificationTemplate) {
    this.selectedTemplate = template;
  }

  // Reset send form
  resetSendForm() {
    this.recipient = '';
    this.selectedChannel = '';
    this.variables = {};
    this.requiredVariables = [];
    this.selectedTemplate = null;
    this.templateDescription = '';
  }

  // Send notification
  async sendNotification() {
    // Validation
    if (!this.selectedTemplate) {
      this.toastService.showToaster('Validation', 'Please select a template', 'error');
      return;
    }

    if (!this.recipient.trim()) {
      this.toastService.showToaster('Validation', 'Please enter recipient', 'error');
      return;
    }

    if (!this.selectedChannel) {
      this.toastService.showToaster(
        'Validation',
        'Please select a channel',
        'error'
      );
      return;
    }


    // Check if all required variables are filled
    for (const variable of this.requiredVariables) {
      if (!this.variables[variable]) {
        this.toastService.showToaster('Validation', `Please fill variable: {{${variable}}}`, 'error');
        return;
      }
    }

    try {
      this.isLoading = true;

      const payload: SendNotificationPayload = {
        templateCode: this.selectedTemplate.code,
        channels: this.selectedChannel,
        recipient: this.recipient,
        variables: this.variables
      };

      this.emailService.sendNotification(payload).subscribe(
        () => {
          this.isLoading = false;
          this.toastService.showToaster('Success', 'Notification sent successfully', 'success');
          this.resetSendForm();
        },
        () => {
          this.isLoading = false;
        }
      );
    } catch (error) {
      this.isLoading = false;
      this.toastService.showToaster('Error', 'Error sending notification: ' + error, 'error');
    }
  }

  // ==================== NOTIFICATION LOGS ====================

  // Load notification logs
  loadNotificationLogs(event: LazyLoadEvent) {
    this.isLoading = true;

    const first = event?.first ?? 0;
    const rows = event?.rows ?? this.rows;
    const page = first / rows + 1;
    let startDate: string | null = null;
    let endDate: string | null = null;

    if (this.rangeDates && this.rangeDates.length === 2) {
      startDate = this.formatDate(this.rangeDates[0]);
      endDate = this.formatDate(this.rangeDates[1]);
    }

    const queryParams = {
      page: page,
      limit: rows,
      search: this.searchText,
      status: this.selectedStatus,
      templateCode: this.selectedTemplateCode,
      channel: this.selectedChannel,
      startDate: startDate,
      endDate: endDate
    };

    this.emailService.getNotificationLogs(queryParams).subscribe(
      (res: any) => {
        this.notificationLogs = res?.data;
        this.totalRecords = res?.totalCount || 0;
        this.isLoading = false;
        this.showLogs = true;
      },
      () => {
        this.isLoading = false;
      }
    );
  }

  totalRecords = 0;
  rows = 10;
  searchText = '';
  selectedStatus: string = '';
  selectedTemplateCode: string = '';
  templateCodeOptions: any = [];
  rangeDates: Date[] | null = null;

  statusOptions = [
    { label: 'All', value: '' },
    { label: 'Sent', value: 'sent' },
    { label: 'Pending', value: 'pending' },
    { label: 'Failed', value: 'failed' },
  ];

  channelOptions = [
    { label: 'All', value: '' },
    { label: 'Email', value: 'email' },
    { label: 'SMS', value: 'sms' },
    { label: 'Whatsapp', value: 'whatsapp' }
  ];


  formatDate(date: Date): string {
    const y = date.getFullYear();
    const m = (date.getMonth() + 1).toString().padStart(2, '0');
    const d = date.getDate().toString().padStart(2, '0');
    return `${y}-${m}-${d}`;
  }

  filterChange(event) {
    console.log(event)
    this.selectedStatus = event.status;
    this.selectedTemplateCode = event.templateCode;
    this.selectedChannel = event.channel;
    this.searchText = event.search;
    this.rangeDates = event.dateRange
    this.table?.reset();
  }

  maxLength = 2000;

  onEditorChange(event: any) {
    const data = event.editor.getData();
    const plainText = this.stripHtml(data);
    if (plainText.length > this.maxLength) {
      const trimmed = plainText.substring(0, this.maxLength);
      this.emailBody = trimmed;
    } else {
      this.emailBody = data;
    }
  }

  stripHtml(html: string): string {
    const div = document.createElement('div');
    div.innerHTML = html;
    return div.innerText || div.textContent || '';
  }

  getPlainTextLength(html: string): number {
    return this.stripHtml(html || '').length;
  }


}
